import React, { useContext, useEffect, useRef, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';

import { BookLocationDocumentValuesContext, RailValuesContext } from 'contexts';
import { useDebounce, useDimensions } from 'hooks';
import { locationLogic, orderUserContentByCfi } from 'utilities';

const useStyles = makeStyles((theme) => {
  return {
    railItem: ({ railItemMinWidth, railItemPaddingLeft }) => ({
      position: 'absolute',
      minWidth: railItemMinWidth,

      paddingLeft: railItemPaddingLeft,
      paddingBottom: '10px',
      [theme.breakpoints.down('sm')]: {
        width: '44px !important',
        minWidth: '44px',
      },
    }),
  };
});

const Rail = ({
  items,
  frame,
  nonce,
  railItemMinWidth,
  railItemPaddingLeft,
  ...props
}) => {
  const classes = useStyles({ railItemMinWidth, railItemPaddingLeft });
  const { currentBookLocationDocument } = useContext(
    BookLocationDocumentValuesContext,
  );

  const railRef = useRef();
  const [orderedItems, setOrderedItems] = useState([]);
  const [positionedElementsUndebounced, setPositionedElementsUndebounced] =
    useState([]);

  /**
   * The rail may take more than one render cycle to "settle" the rail item positions.
   * By debouncing, we can ensure that the positionedElements array (provided through RailValuesContext) is correct.
   */
  const { value: positionedElements } = useDebounce(
    positionedElementsUndebounced,
    20,
  );

  // Watch the document's container for updates
  const { dimensions, dimensionsUpdating } = useDimensions(
    frame?.parentElement?.parentElement,
  );

  // Computes an ideal pixel offset for each item and sorts them so that they can be rendered in order.
  useEffect(() => {
    setPositionedElementsUndebounced([]);

    if (!frame) {
      return;
    }

    // Compute the ideal top (in pixels) for each item
    for (let i = 0; i < items.length; i += 1) {
      let idealTop = 0;
      const { bookLocation } = items[i];
      const cfi = bookLocation.cfi;
      const pageElement = bookLocation.page?.element;

      if (cfi) {
        try {
          idealTop = Math.round(locationLogic.getPixelsForCfi(frame, cfi));
        } catch (e) {
          console.error(
            'Failed to get cfi position for rail item',
            items[i],
            cfi,
          );
        }
      } else if (pageElement) {
        idealTop = frame.contentDocument
          ?.getElementById(pageElement)
          ?.getBoundingClientRect().top;
      }

      items[i].idealTop = idealTop;
    }

    // First order items by CFI
    const itemsOrderedByCfi = orderUserContentByCfi(
      frame,
      currentBookLocationDocument,
      items,
    );

    // Then order items by their computed ideal top pixel value
    const itemsOrderedByIdealTop = itemsOrderedByCfi.sort(
      (a, b) => a.idealTop - b.idealTop,
    );

    setOrderedItems(itemsOrderedByIdealTop);
  }, [items, currentBookLocationDocument, frame, dimensions, nonce]);

  // Ensures that no rail items overlap and that the rail has enough width.
  useEffect(() => {
    // Ensure rail has child elements.
    if (!railRef.current?.children) {
      return;
    }

    // Loop through all item objects
    for (let i = 0; i < orderedItems.length; i += 1) {
      const itemElement = railRef.current.children[i];
      const previousItemElement = i
        ? railRef.current.children[i - 1]
        : undefined;

      // Constrain by the position of the previous item's bottom
      let previousBottom = 0;
      if (previousItemElement) {
        previousBottom =
          parseInt(previousItemElement.style.top, 10) +
          previousItemElement.clientHeight;
      }

      // Set the actual top position based on the ideal position and the previous item's bottom, whichever is greater
      const actualTop = Math.max(previousBottom, orderedItems[i].idealTop);
      itemElement.style.top = `${actualTop}px`;

      // Tell any component using rail context that this element has been positioned.
      // This references the first child element, not the railItem div wrapper.
      setPositionedElementsUndebounced((elements) => [
        ...elements,
        itemElement.children[0],
      ]);
    }

    // Because we use position: absolute on the items, the rail has no natural width.
    // This ensure that it is at least the width of the largest item.
    // More intentional styling will likely override this, but it ensures the items are visible.
    const railMinWidth = Math.max(
      0,
      ...orderedItems.map(
        (_, i) => railRef.current.children[i].getBoundingClientRect().width,
      ),
    );

    railRef.current.style.minWidth = `${railMinWidth}px`;
  }, [frame, orderedItems]);

  const shouldHideRail =
    dimensionsUpdating && !currentBookLocationDocument?.isFixedWidth;

  return (
    <div
      className={classes.rail}
      style={shouldHideRail ? { opacity: 0 } : {}}
      ref={railRef}
      {...props}
    >
      <RailValuesContext.Provider
        value={{
          isWithinRail: true,
          positionedElements,
        }}
      >
        {orderedItems.map(({ element, bookLocation, key }) => (
          <div
            className={classes.railItem}
            data-rs-cfi={bookLocation.cfi}
            key={key}
          >
            {element}
          </div>
        ))}
      </RailValuesContext.Provider>
    </div>
  );
};

Rail.propTypes = {
  // a list of objects that contain an element, a cfi, and a key (for rendering)
  items: PropTypes.array.isRequired,

  // reference to an iframe element
  frame: PropTypes.object,

  /**
   * The nonce should force a rerender whenever it changes.
   *
   * The rail component does not need to know why it needs to rerender,
   * only that it needs to rerender when this value changes.
   *
   * The consuming component can combine multiple properties if necessary to create this value,
   * and can update it when any of them change.
   */
  nonce: PropTypes.any,
  railItemMinWidth: PropTypes.number,
  railItemPaddingLeft: PropTypes.number,
};

export default memo(Rail);
